import { LeadInfoField } from "@/types/leads/lead-info";
import { ApiService } from "./ApiService";

export interface Insight {
  id: number;
  name: string | null;
  description: string | null;
  fields: LeadInfoField[] | null;
  is_default: boolean | null;
  created_at: string;
  owner?: {
    id: string;
    full_name: string;
    email: string;
  };
  dependent_agents?: Array<{ id: string; name: string }>;
}

export interface InsightGenieContext {
  agentId?: string;
  name?: string;
  description?: string;
  purpose?: string;
  systemPrompt?: string;
}

export interface InsightGenerationResult {
  name: string;
  description: string;
  fields: LeadInfoField[];
  confidence: number;
}

export class InsightService {
  static async all(
    filters?: Record<string, any>,
    limit: number = 50,
    offset: number = 0,
  ): Promise<{ insights: Insight[]; count: number }> {
    try {
      const response = await ApiService.invoke<{
        insights: Insight[];
        count: number;
      }>({
        resource: "insights",
        action: "all",
        data: {
          limit,
          offset,
          filters,
        },
      });

      return {
        insights: response?.insights || [],
        count: response?.count || 0,
      };
    } catch (error) {
      console.error("[InsightService] Error fetching all insights:", error);
      return { insights: [], count: 0 };
    }
  }

  static async get(insightId: number): Promise<Insight | null> {
    try {
      const response = await ApiService.invoke<{ insight: Insight }>({
        resource: "insights",
        action: "get",
        id: String(insightId),
      });

      return response?.insight || null;
    } catch (error) {
      console.error("[InsightService] Error getting insight:", error);
      return null;
    }
  }

  static async getDefault(): Promise<Insight | null> {
    try {
      const response = await ApiService.invoke<{ insight: Insight }>({
        resource: "insights",
        action: "get",
        data: {
          filters: { is_default: true },
        },
      });

      return response?.insight || null;
    } catch (error) {
      console.error("[InsightService] Error getting default insight:", error);
      return null;
    }
  }

  static async getByAgent(agentId: string): Promise<Insight | null> {
    try {
      const response = await ApiService.invoke<{ items: Insight[] }>({
        resource: "insights",
        action: "all",
        data: {
          filters: { agent_id: agentId },
          limit: 1,
        },
      });

      const insights = response?.items || [];
      return insights.length > 0 ? insights[0] : null;
    } catch (error) {
      console.error("[InsightService] Error getting agent insight:", error);
      return null;
    }
  }

  static async create(
    name: string,
    description?: string,
    fields?: LeadInfoField[],
    isDefault?: boolean,
  ): Promise<Insight | null> {
    try {
      const response = await ApiService.invoke<{ insight: Insight }>({
        resource: "insights",
        action: "create",
        data: {
          name,
          description,
          fields,
          is_default: isDefault,
        },
      });

      return response?.insight || null;
    } catch (error) {
      console.error("[InsightService] Error creating insight:", error);
      return null;
    }
  }

  static async createDefault(name: string, description?: string, fields?: LeadInfoField[]): Promise<Insight | null> {
    return this.create(name, description, fields, true);
  }

  static async update(
    insightId: number,
    updates: {
      name?: string;
      description?: string;
      fields?: LeadInfoField[];
      is_default?: boolean;
    },
  ): Promise<Insight | null> {
    try {
      const response = await ApiService.invoke<{ insight: Insight }>({
        resource: "insights",
        action: "update",
        id: String(insightId),
        data: updates,
      });

      return response?.insight || null;
    } catch (error) {
      console.error("[InsightService] Error updating insight:", error);
      return null;
    }
  }

  static async delete(insightId: number): Promise<boolean> {
    try {
      const response = await ApiService.invoke<{ success: boolean }>({
        resource: "insights",
        action: "delete",
        id: String(insightId),
      });

      return response?.success ?? false;
    } catch (error) {
      console.error("[InsightService] Error deleting insight:", error);
      return false;
    }
  }

  static async attachToAgent(agentId: string, insightId: number): Promise<boolean> {
    try {
      const response = await ApiService.invoke<{ success: boolean }>({
        resource: "insights",
        action: "attach",
        data: { agentId, insightId },
      });

      return response?.success ?? false;
    } catch (error) {
      console.error("[InsightService] Error attaching insight to agent:", error);
      return false;
    }
  }

  static async detachFromAgent(agentId: string): Promise<boolean> {
    try {
      const response = await ApiService.invoke<{ success: boolean }>({
        resource: "insights",
        action: "detach",
        data: { agentId },
      });

      return response?.success ?? false;
    } catch (error) {
      console.error("[InsightService] Error detaching insight from agent:", error);
      return false;
    }
  }

  static async generate(
    description: string,
    genieContext?: InsightGenieContext,
  ): Promise<InsightGenerationResult | null> {
    try {
      const { agentId, ...unsavedContext } = genieContext ?? {};
      const response = await ApiService.invoke<InsightGenerationResult>({
        resource: "insights",
        action: "generate",
        data: { description, agentId, genieContext: unsavedContext },
      });

      return response || null;
    } catch (error) {
      console.error("[InsightService] Error generating insight:", error);
      return null;
    }
  }

  static async createFromDescription(description: string, genieContext?: InsightGenieContext): Promise<Insight | null> {
    try {
      const generated = await this.generate(description, genieContext);
      if (!generated) return null;

      return this.create(generated.name, generated.description, generated.fields);
    } catch (error) {
      console.error("[InsightService] Error creating insight from description:", error);
      return null;
    }
  }
}
